import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import BottomSheet from './BottomSheet';

// Country name for the device's region (e.g. de-DE → "Germany")
function defaultCountry() {
  try {
    const locale = navigator.languages?.[0] || navigator.language || '';
    const region = new Intl.Locale(locale).maximize().region;
    return region ? new Intl.DisplayNames(['en'], { type: 'region' }).of(region) : '';
  } catch {
    return '';
  }
}

const inputClass = 'w-full rounded-xl border border-gray-300 px-3 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

/**
 * Sheet for creating a client location (facility) inline.
 * Country is required: foreign-travel allowances depend on it.
 */
function NewLocationSheet({ open, clientName, initialLabel = '', onCancel, onSave }) {
  const [fields, setFields] = useState({ label: '', city: '', country: '', address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setFields({ label: initialLabel, city: '', country: defaultCountry(), address: '' });
  }, [open, initialLabel]);

  const set = (key) => (e) => setFields(f => ({ ...f, [key]: e.target.value }));
  const valid = fields.label.trim() && fields.city.trim() && fields.country.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await onSave({
        label: fields.label.trim(),
        city: fields.city.trim(),
        country: fields.country.trim(),
        address: fields.address.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onCancel}
      title={clientName ? `New facility · ${clientName}` : 'New facility'}
      footer={
        <button
          type="submit"
          form="new-location-form"
          disabled={!valid || saving}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50"
        >
          {saving && <Loader className="w-5 h-5 animate-spin" />}
          Create facility
        </button>
      }
    >
      <form id="new-location-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name / label *</label>
          <input value={fields.label} onChange={set('label')} placeholder="e.g. KEM plant" className={inputClass} autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <input value={fields.city} onChange={set('city')} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
            <input value={fields.country} onChange={set('country')} className={inputClass} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input value={fields.address} onChange={set('address')} placeholder="Optional" className={inputClass} />
        </div>
      </form>
    </BottomSheet>
  );
}

export default NewLocationSheet;
